/**
 * useLiveUpdates — WebSocket realtime hook.
 *
 * Apre una connessione WS a `${REACT_APP_BACKEND_URL}/api/ws?token=...`
 * usando la stessa sessione HTTP dell'utente (cookie httpOnly non è leggibile
 * da JS: il backend accetta anche il token via query string per WS).
 *
 * Uso tipico:
 *   useLiveUpdates({
 *     rooms: [`cantiere:${cantId}`, "economia"],
 *     onEvent: (msg) => {
 *       if (msg.type.startsWith("attivita.")) qc.invalidateQueries(["attivita-crono", cantId]);
 *       if (msg.type === "economia.changed") qc.invalidateQueries({ queryKey: ["cantiere-econ"] });
 *     }
 *   });
 *
 * Il token viene recuperato via /api/auth/ws-token (endpoint dedicato per WS).
 * Se il backend non lo supporta, il client passa in modalità no-op silenziosa.
 */
import { useEffect, useRef } from "react";
import api from "@/api";

const WS_BACKOFF_MS = [1000, 2000, 5000, 10000, 15000];

export function useLiveUpdates({ rooms = [], onEvent, enabled = true }) {
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const closedRef = useRef(false);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!enabled) return undefined;
    closedRef.current = false;

    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const wsProto = backendUrl.startsWith("https") ? "wss" : "ws";
    const host = backendUrl.replace(/^https?:\/\//, "");
    if (!host) return undefined;

    let cancelled = false;

    async function connect() {
      if (cancelled || closedRef.current) return;
      let token;
      try {
        const r = await api.get("/auth/ws-token");
        token = r.data?.token;
      } catch {
        // fallback: no WS
        return;
      }
      if (!token || cancelled) return;
      const url = `${wsProto}://${host}/api/ws?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        retryRef.current = 0;
        rooms.forEach((room) => {
          try {
            ws.send(JSON.stringify({ action: "subscribe", room }));
          } catch {
            /* ignore */
          }
        });
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          onEventRef.current?.(msg);
        } catch {
          /* ignore */
        }
      };
      ws.onerror = () => {
        try { ws.close(); } catch { /* ignore */ }
      };
      ws.onclose = () => {
        wsRef.current = null;
        if (closedRef.current || cancelled) return;
        const delay = WS_BACKOFF_MS[Math.min(retryRef.current, WS_BACKOFF_MS.length - 1)];
        retryRef.current += 1;
        setTimeout(connect, delay);
      };
    }

    connect();

    return () => {
      cancelled = true;
      closedRef.current = true;
      const ws = wsRef.current;
      if (ws) {
        try { ws.close(); } catch { /* ignore */ }
        wsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, rooms.join("|")]);
}
